<template>
  <b-modal
    :id="modalId"
    title="Create Web AR Embed"
    size="lg"
    role="dialog"
    modal-class="regular"
    centered
    no-close-on-backdrop
    :ok-title="okTitle"
    :ok-only="okOnly"
    :ok-disabled="okDisabled"
    @ok="onModalOk"
    @shown="onModalShown"
  >
    <template slot="default">
      <div class="custom-modal-content">
        <div v-if="isStateInitial">
          <b-row>
            <b-col>
              <b-container class="label">Title:<span class="important-label">*</span></b-container>
              <b-form-input
                ref="embedTitle"
                v-model="$v.embed.title.$model"
                :state="validationUtils.validateState($v.embed, 'title')"
                placeholder="Enter a title"
                class="modal-input"
              ></b-form-input>
              <b-form-invalid-feedback>
                This is a required field.
              </b-form-invalid-feedback>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-container class="label">Description:</b-container>
              <b-form-textarea
                v-model="embed.description"
                placeholder="Enter a description (optional)"
                class="modal-input"
              ></b-form-textarea>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-container class="label" for="">Model:<span class="important-label">*</span></b-container>
              <v-select
                id="embed-model-select"
                v-model="$v.embedModelBeingSelected.model.$model"
                class="modal-input mb-2 mr-sm-2 mb-sm-0"
                placeholder="-- Select a model --"
                :options="allWebARModels"
                :value="null"
                label="name"
              >
              </v-select>
              <input
                type="hidden"
                class="form-control"
                v-bind:class="{ 'is-invalid': $v.embedModelBeingSelected.model.$error }"
              />
              <div
                class="error invalid-feedback"
                v-if="$v.embedModelBeingSelected.model.$error"
              >
                Please select a model.
              </div>
            </b-col>
          </b-row>
        </div>
        <div v-if="isStatePosting">
          <b-container class="custom-modal-message">
            <b-alert show variant="info">
              Please wait while the embed is being added...
            </b-alert>
          </b-container>
        </div>
        <div v-if="isStateSuccess">
          <b-container class="custom-modal-message">
            <b-alert show variant="success">
              The embed has been added successfully!
            </b-alert>
          </b-container>
        </div>
        <div v-if="isStateFailed">
          <b-container class="custom-modal-message">
            <b-alert show variant="danger">
              The embed couldn't be added at this time. Please try again later.
            </b-alert>
          </b-container>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import * as harpraApi from "@/services/api.service";
import * as stringUtils from "@/utils/string-utils.js";
import * as validationUtils from "@/utils/validation-utils.js"
import modals from '@/enums/modal-types'

import Vue from "vue"
import vSelect from "vue-select"
import "vue-select/dist/vue-select.css";

import { required } from 'vuelidate/lib/validators'

Vue.component('v-select', vSelect)

const STATE_INITIAL = 0, STATE_POSTING = 1, STATE_SUCCESS = 2, STATE_FAILED = 3;

export default {
  name: "AddWebAREmbedModal",
  props: {
    modalId: String,
  },
  data() {
    return {
      embed: this.createEmptyEmbed(),
      embedModelBeingSelected: this.createEmptyEmbedModelBeingSelected(),
      allWebARModels: [],
      currentState: STATE_INITIAL,
      okTitle: "",
      okDisabled: false,
      okOnly: false,
    }
  },
  computed: {
    stringUtils() {
      return stringUtils;
    },
    validationUtils() {
      return validationUtils;
    },
    isStateInitial() {
      return this.currentState === STATE_INITIAL;
    },
    isStatePosting() {
      return this.currentState === STATE_POSTING;
    },
    isStateSuccess() {
      return this.currentState === STATE_SUCCESS;
    },
    isStateFailed() {
      return this.currentState === STATE_FAILED;
    },
  },
  validations: {
    embed: {
      title: {
        required,
      },
      model: {
        required,
      },
    },
    embedModelBeingSelected: {
      model: {
        required,
      }
    },
  },
  methods: {
    onModalShown() {
      this.transitionToState(STATE_INITIAL);
    },
    transitionToState(state) {
      this.currentState = state;

      switch (state) {
        case STATE_INITIAL: {
          this.okTitle = "Create";
          this.okDisabled = false;
          this.okOnly = false;
          this.resetForm();
          break;
        }

        case STATE_POSTING: {
          this.okDisabled = true;
          this.okOnly = true;
          break;
        }

        case STATE_SUCCESS:
        case STATE_FAILED: {
          this.okTitle = "Close";
          this.okDisabled = false;
          this.okOnly = true;
          break;
        }
      }
    },
    resetForm() {
      this.embed = this.createEmptyEmbed();
      this.embedModelBeingSelected = this.createEmptyEmbedModelBeingSelected();
      this.populateAllWebARModels();
      this.$v.$reset();

      this.$forceNextTick(() => {
        this.$refs.embedTitle.focus();
      });
    },
    createEmptyEmbed() {
      return {
        title: "",
        description: "",
        model: this.createEmptyEmbedModelBeingSelected(),
      };
    },
    createEmptyEmbedModelBeingSelected() {
      return {
        model: null,
      };
    },
    populateAllWebARModels() {
      this.$AuthService.getAccessToken(
        async () => {
          this.allWebARModels =
            await harpraApi.getModels(
              this.$AuthService.token.accessToken,
              harpraApi.StorageType.WebAR);
        });
    },
    onModalOk(modalEvent) {
      modalEvent.preventDefault();

      switch (this.currentState) {
        case STATE_INITIAL: {
          this.embed.model = null;

          if (this.embedModelBeingSelected.model != null) {
            this.embed.model = {
              modelId: this.embedModelBeingSelected.model.guid,
              modelName: this.embedModelBeingSelected.model.name,
              modelFormat: this.embedModelBeingSelected.model.format,
            }
          }

          this.$v.$touch();

          if (this.$v.$anyError) {
            return;
          }

          this.addEmbed();

          break;
        }
        case STATE_SUCCESS:
        case STATE_FAILED: {
          this.$nextTick(() => {
            this.$bvModal.hide(modals.addWebAREmbedModal)
          });

          break;
        }
      }
    },
    addEmbed() {
      this.transitionToState(STATE_POSTING);

      this.$AuthService.getAccessToken(async () => {
        try {
          const formData = this.embed;

          await harpraApi.postWebAREmbed(formData);

          this.transitionToState(STATE_SUCCESS);

          this.$emit("web-ar-embed-posted");
        } catch (error) {
          this.transitionToState(STATE_FAILED);

          this.$emit("web-ar-embed-post-failed");
        }
      }, () => {
        this.transitionToState(STATE_FAILED);

        this.$emit("web-ar-embed-post-failed");
      });
    },
  },
  created() {
  },
  mounted() {
  },
}
</script>
